import { usePageStore, type LayoutKey } from "@astrox/ui-vue/store";
import { createRouter, createWebHistory } from "vue-router";
import { applicationRoute } from "./features/application/presentation/routes/applicationRoute";
import { authRoute, authRouteName } from "./features/auth/presentation/routes/authRoute";
import { useAuthStore } from "./features/auth/presentation/stores/authStore";
import { clientCategoryRoute } from "./features/client-category/presentation/routes/clientCategoryRoute";
import { clientServiceRoute } from "./features/client-service/presentation/routes/clientServiceRoute";
import { clientRoute } from "./features/client/presentation/routes/clientRoute";
import { clientUserRoute } from "./features/client/presentation/routes/clientUserRoute";
import { dashboardRoute } from "./features/dashboard/presentation/routes/dashboardRoute";
import { departmentRoute } from "./features/department/presentation/routes/departmentRoute";
import { employeeRoute } from "./features/employee/presentation/routes/employeRoute";
import { levelRoute } from "./features/level/presentation/routes/levelRoute";
import { officeRoute } from "./features/office/presentation/routes/officeRoute";
import { positionRoute } from "./features/position/presentation/routes/positionRoute";
import { projectRoute } from "./features/project/presentation/routes/projectRoute";
import { recruitmentHiringRoute } from "./features/recruitment-hiring/presentation/routes/recruitmentHiringRoute";
import { recruitmentRequestRoute } from "./features/recruitment-request/presentation/routes/recruitmentRequestRoute";
import { recruitmentScreeningRoute } from "./features/recruitment-screening/presentation/routes/recruitmentScreeningRoute";
import { recruitmentSettingRoute } from "./features/recruitment-setting/presentation/routes/recruitmentSettingRoute";
import { sectionRoute } from "./features/section/presentation/routes/sectionRoute";
import { terminationCategoryRoute } from "./features/termination-category/presentation/routes/terminationCategoryRoute";
import { terminationReasonRoute } from "./features/termination-reason/presentation/routes/terminationReasonRoute";
import { terminationRequestRoute } from "./features/termination-request/presentation/routes/terminationRequestRoute";
import { terminationSettingRoute } from "./features/termination-setting/presentation/routes/terminationSettingRoute";
import { vendorCategoryRoute } from "./features/vendor-category/presentation/routes/vendorCategoryRoute";
import { vendorServiceRoute } from "./features/vendor-service/presentation/routes/vendorServiceRoute";
import { vendorRoute } from "./features/vendor/presentation/routes/vendorRoute";
import { vendorUserRoute } from "./features/vendor/presentation/routes/vendorUserRoute";
import { workScheduleRoute } from "./features/work-schedule/presentation/routes/workScheduleRoute";
import { redirectToSso } from "./lib/auth/redirectToSso";
import i18n from "./i18n";
import { filterMenuByPermission, menuUtil } from "./utils/menuUtil";

export function setupRouter() {
  const router = createRouter({
    history: createWebHistory(import.meta.env.VITE_BASE_URL || "/"),
    routes: [
      { path: "/", redirect: "/dashboard" },
      ...authRoute,
      ...dashboardRoute,
      ...departmentRoute,
      ...sectionRoute,
      ...levelRoute,
      ...positionRoute,
      ...clientRoute,
      ...clientServiceRoute,
      ...clientCategoryRoute,
      ...clientUserRoute,
      ...projectRoute,
      ...vendorRoute,
      ...vendorServiceRoute,
      ...vendorCategoryRoute,
      ...employeeRoute,
      ...officeRoute,
      ...workScheduleRoute,
      ...recruitmentRequestRoute,
      ...applicationRoute,
      ...vendorUserRoute,
      ...recruitmentHiringRoute,
      ...recruitmentSettingRoute,
      ...terminationRequestRoute,
      ...terminationSettingRoute,
      ...terminationCategoryRoute,
      ...terminationReasonRoute,
      ...recruitmentScreeningRoute,
    ],
  });

  let isAppInitialized = false;

  function initAppMetadataOnce(pageStore: ReturnType<typeof usePageStore>, authStore: ReturnType<typeof useAuthStore>) {
    if (isAppInitialized) return;

    pageStore.initAppMetadata({
      appName: import.meta.env.VITE_APP_NAME ?? "My App",
      appLogo: `${import.meta.env.VITE_PUBLIC_URL || ""}/astraworld-icon.jpg`,
    });

    // Daftarkan logout handler sekali saja — layout (AdminSidebarLayout)
    // tinggal panggil pageStore.triggerLogout() tanpa perlu tahu implementasi auth-nya.
    pageStore.setLogoutHandler(async () => {
      if (typeof (authStore as any).logout === "function") {
        await (authStore as any).logout();
      } else {
        localStorage.removeItem("access_token");
        (authStore as any).profile = null;
      }

      pageStore.clearUserInfo();
      redirectToSso();
    });

    isAppInitialized = true;
  }

  router.beforeEach(async (to) => {
    const pageStore = usePageStore();
    const authStore = useAuthStore();

    initAppMetadataOnce(pageStore, authStore);

    // Route khusus SSO callback: skip auth-check & fetch-profile
    // (profile biasanya belum ada saat ini), tapi TETAP lanjut ke bawah
    // supaya page config (title/breadcrumbs/layout) ikut ke-set.
    const isAuthCallback = to.matched.some((record) => record.path === "/auth/callback" || record.name === authRouteName.callback);

    if (!isAuthCallback) {
      const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
      const accessToken = localStorage.getItem("access_token");
      const isAuthenticated = !!accessToken;

      if (requiresAuth && !isAuthenticated) {
        redirectToSso(to.fullPath);
        return false;
      }

      // Autoload profile - hanya fetch kalau sudah login tapi profile belum dimuat
      if (isAuthenticated && !authStore.profile && !authStore.isLoading) {
        try {
          await authStore.fetchProfile();
        } catch {
          // Token invalid/expired - bersihkan dan redirect ke SSO
          localStorage.removeItem("access_token");
          pageStore.clearUserInfo();
          redirectToSso(to.fullPath);
          return false;
        }
      }
    }

    // Feed minimal user info ke pageStore setiap kali profile tersedia,
    // supaya AdminSidebarLayout bisa menampilkan info user tanpa
    // bergantung langsung ke authStore.
    if (authStore.profile) {
      pageStore.setUserInfo({
        name: (authStore.profile as any).name ?? (authStore.profile as any).fullName ?? "-",
        email: (authStore.profile as any).email,
        avatarUrl: (authStore.profile as any).avatarUrl ?? (authStore.profile as any).avatar,
      });
    } else {
      pageStore.clearUserInfo();
    }

    const breadcrumbs = typeof to.meta.breadcrumbs === "function" ? to.meta.breadcrumbs(to, router) : to.meta.breadcrumbs;

    // Filter menu by the current user's permissions here — pageStore just
    // receives the already-filtered list and stores it as-is.
    const fullMenu = menuUtil(i18n.global.t);
    const filteredMenu = filterMenuByPermission(fullMenu, authStore.permissions as any);

    pageStore.updatePageConfig({
      title: to.meta.title as string | undefined,
      activeMenu: to.meta.menu as any,
      menuOptions: filteredMenu,
      layout: to.meta.layout as LayoutKey | "plain" | undefined,
      breadcrumbs: breadcrumbs ?? [],
      route: to,
    });

    return true;
  });

  return router;
}
